import type { AgentsBoard } from '../../../api/agentSchemas';
import type { AgentLine } from '../agentLine';
import { normalize } from '../normalize';
import type { FinanceFacts, FinanceNeed } from './facts';
import { anio, ayer, comparar, gastos, gastosFijos, hoy, mayorGasto, mejorar, mesPasado, planilla, resultado, resumen, semana, ventas } from './repliesMoney';
import { cliente, equipo, faltas, impuesto, metodo, porCobrar, servicio, ticket } from './repliesPeople';
import { mercurio, type Reply } from './replyKit';

/**
 * What an owner can ask Mercurio about money. v1 is rules, not a model: a
 * question is matched on the words that matter, the numbers come from the same
 * reports the Finanzas screens read, and the voice says the part of the answer
 * that never changes while the figures go on the screen. Order is meaning:
 * the first intent that matches answers, so the specific ones come first.
 */

export type FinanceIntentId =
  | 'sin-info'
  | 'comparar'
  | 'mes-pasado'
  | 'anio'
  | 'ayer'
  | 'semana'
  | 'hoy'
  | 'mayor-gasto'
  | 'planilla'
  | 'gastos-fijos'
  | 'por-cobrar'
  | 'metodo'
  | 'servicio'
  | 'equipo'
  | 'cliente'
  | 'ticket'
  | 'faltas'
  | 'impuesto'
  | 'resultado'
  | 'gastos'
  | 'ventas'
  | 'resumen'
  | 'mejorar'
  | 'finanzas-otro';

export type FinanceIntent = {
  id: FinanceIntentId;
  test: RegExp;
  /** The numbers the answer is made of; none for a topic the business has no data for. */
  needs: readonly FinanceNeed[];
  reply: Reply;
};

/** A pattern that never matches: these intents are reached by the router, never by a keyword. */
const NEVER = /(?!)/;

const MONEY_CUE = '(cuanto|cobr|vend|ingres|entr|gan|factur|caja|venta|plata|dinero)';
const LOSS_CUE = '(cuanto|dinero|plata|pierd|perdi|costo|valor)';
const MISSED = '(no ?show|no llegaron|no vinieron|faltaron|cancelad|cancelaron|cancelacion|planton|inasisten)';

const INTENTS: readonly FinanceIntent[] = [
  {
    id: 'sin-info',
    test: /\bdeudas?\b|\bdebo\b|prestamo|credito|hipoteca|inventario|\bstock\b|proveedor|presupuesto|proyeccion|pronostico|flujo de caja|cuenta bancaria|cuanto vale|valor de mi negocio/,
    needs: [],
    reply: () => mercurio('sin-info'),
  },
  {
    id: 'comparar',
    test: /compar|(mas|menos|mejor|peor|igual) que (el )?mes|contra (el )?mes|frente al mes|\bvs\b/,
    needs: ['mes', 'mismos-dias'],
    reply: comparar,
  },
  { id: 'mes-pasado', test: /mes (pasado|anterior)|ultimo mes/, needs: ['mes-pasado'], reply: mesPasado },
  { id: 'anio', test: /\bano\b|anual/, needs: ['anio'], reply: anio },
  { id: 'ayer', test: new RegExp(`^(?=.*\\bayer\\b)(?=.*${MONEY_CUE})`), needs: ['ayer'], reply: ayer },
  { id: 'semana', test: new RegExp(`^(?=.*semana)(?=.*${MONEY_CUE})`), needs: ['semana'], reply: semana },
  { id: 'hoy', test: new RegExp(`^(?=.*\\bhoy\\b)(?=.*${MONEY_CUE})`), needs: ['hoy'], reply: hoy },
  {
    id: 'mayor-gasto',
    test: /en que gasto|donde (se )?(me )?(va|gasto|esta)|mayor gasto|gasto (mas )?(grande|alto|fuerte|pesa)|gasto mas|mas caro/,
    needs: ['gastos'],
    reply: mayorGasto,
  },
  { id: 'planilla', test: /planilla|nomina|sueldo|salario|pago a (mi )?(equipo|gente|empleados|personal)/, needs: ['gastos'], reply: planilla },
  {
    id: 'gastos-fijos',
    test: /gastos? fijos?|se repiten|recurrente|cada mes.*gasto|gasto.*cada mes|gastos? mensuales?/,
    needs: ['gastos'],
    reply: gastosFijos,
  },
  {
    id: 'por-cobrar',
    test: /por cobrar|me deben|me debe|quien debe|pendientes? de cobro|cobros? pendientes?|cuentas? pendientes?|saldo pendiente|sin cobrar|fiado/,
    needs: ['mes'],
    reply: porCobrar,
  },
  {
    id: 'metodo',
    test: /metodos? de pago|medios? de pago|formas? de pago|como (me )?(pagan|pagaron|cobran)|efectivo|con tarjeta|yappy|transferencia|banco aliado/,
    needs: ['mes'],
    reply: metodo,
  },
  {
    id: 'servicio',
    test: /servicios? (mas|que mas|mejor|top)|(mas|mejor) (vendido|servicio)|servicio.*(deja|vende|genera|produce)|que se vende mas|mas vendido|producto mas|plato mas|que (vendo|vende) mas/,
    needs: ['mes'],
    reply: servicio,
  },
  {
    id: 'equipo',
    test: /quien (vende|factura|genera|cobra|trae|produce)|mejor (barbero|barbera|empleado|empleada|estilista|vendedor|vendedora|terapeuta|coach|mesero|persona|trabajador)|empleado del mes|rendimiento del equipo|desempeno/,
    needs: ['mes'],
    reply: equipo,
  },
  {
    id: 'cliente',
    test: /mejor cliente|mayor cliente|clientes? (que )?mas|clientes? (top|vip|fieles?)|quien (me )?compra mas|quien me compra/,
    needs: ['mes'],
    reply: cliente,
  },
  { id: 'ticket', test: /ticket|promedio|cada (cobro|cliente|visita|venta)/, needs: ['mes'], reply: ticket },
  {
    id: 'faltas',
    test: new RegExp(`^(?=.*${MISSED})(?=.*${LOSS_CUE})`),
    needs: ['mes'],
    reply: faltas,
  },
  { id: 'impuesto', test: /itbms|impuesto|\biva\b|facturas? emitid|cuantas facturas|facturas? del mes/, needs: ['mes'], reply: impuesto },
  {
    id: 'resultado',
    test: /me queda|queda de|ganancia|utilidad|\bneto\b|margen|ganando|perdiendo|rentab|en rojo|en verde|resultado/,
    needs: ['mes'],
    reply: resultado,
  },
  {
    id: 'gastos',
    test: /gast(?:os|ado|ados|aste|o|e)\b|egreso|cuanto (he )?(pague|pagado)|costos?\b|salidas? de dinero/,
    needs: ['mes'],
    reply: gastos,
  },
  {
    id: 'ventas',
    test: /vend|venta|ingres|entrado|me entro|\bentra\b|cobrad|cobre\b|cobro|factur|caja|ganad|\bgane\b|\bgano\b|cuanto llevo/,
    needs: ['mes'],
    reply: ventas,
  },
  {
    id: 'resumen',
    test: /como (va|vamos|voy|van|me va) (el negocio|mi negocio|todo|el mes|mis numeros|los numeros|las ventas|mis ventas|las finanzas|mis finanzas)|como (voy|vamos|estamos|andamos)$|resumen|balance|finanza|numeros|cifras|plata|dinero/,
    needs: ['mes'],
    reply: resumen,
  },
  { id: 'mejorar', test: NEVER, needs: ['mes'], reply: mejorar },
  { id: 'finanzas-otro', test: NEVER, needs: ['mes'], reply: resumen },
];

const BY_ID: ReadonlyMap<FinanceIntentId, FinanceIntent> = new Map(INTENTS.map((intent) => [intent.id, intent]));

export function isFinanceId(id: string): id is FinanceIntentId {
  return BY_ID.has(id as FinanceIntentId);
}

/** The numbers an intent has to fetch before it can answer. */
export function financeNeedsOf(id: FinanceIntentId): readonly FinanceNeed[] {
  return BY_ID.get(id)?.needs ?? [];
}

export function matchFinance(text: string): FinanceIntent | undefined {
  const said = normalize(text);
  return INTENTS.find((intent) => intent.test.test(said));
}

/** The numbers to fetch before answering, or null when the question is not about money. */
export function financeNeeds(text: string): readonly FinanceNeed[] | null {
  return matchFinance(text)?.needs ?? null;
}

/**
 * Mercurio's answer, or null when the question is not about money (or, for
 * "how is the business", when the owner may not see the numbers: Júpiter
 * answers that one with the work to review).
 */
export function financeReply(text: string, facts: FinanceFacts, board: AgentsBoard): AgentLine | null {
  const intent = matchFinance(text);
  return intent ? financeReplyFor(intent.id, facts, board) : null;
}

/** Mercurio's answer for an intent already chosen; null only for "how is the business" when the owner may not see the numbers. */
export function financeReplyFor(id: FinanceIntentId, facts: FinanceFacts, board: AgentsBoard): AgentLine | null {
  const intent = BY_ID.get(id);
  if (!intent) return null;
  if (id === 'sin-info') return intent.reply({ facts: EMPTY, board });
  if (facts.status === 'locked') return id === 'resumen' ? null : mercurio('sin-permiso');
  if (facts.status === 'error') return mercurio('error-cuentas');
  return intent.reply({ facts, board });
}

const EMPTY = { status: 'ready', now: new Date(0), reports: {} } as const;
